/**
 * Server-side feature gate middleware for oRPC procedures.
 * Enforces plan-based feature limits at the API level (not just client UI).
 * @package @app/api
 *
 * Throws ORPCError('FORBIDDEN') with a `data.kind === 'FEATURE_GATE'`
 * payload so the client's error interceptor can detect a plan-gate hit
 * and route the user to /pricing?limit=<feature> with an upgrade modal.
 */

import { track } from '@app/analytics/server';
import { FEATURE_DESCRIPTIONS, PLAN_FEATURES, getFeatureLimit, hasFeature } from '@app/billing-config';
import type { PlanFeatures, PlanName } from '@app/billing-config';
import { ORPCError } from '@orpc/server';
import type { Middleware } from '@orpc/server';
import type { Context } from './context';

/**
 * oRPC Middleware signature is heavily generic (TInContext / TOutContext /
 * TInput / TOutput / TErrorConstructorMap / TMeta). For these gate factories
 * we don't add new context keys or transform input/output — we just inspect
 * the existing context and optionally `throw`. The simplest type-correct
 * shape is `Middleware<any, any, any, any, any, any>` (i.e. `AnyMiddleware`)
 * with the actual context type asserted internally.
 */
type GateMiddleware = Middleware<any, any, any, any, any, any>;

/**
 * Wire-format payload attached to ORPCError.data when a gate fires.
 * Kept narrow + JSON-safe so the client can pattern-match without
 * importing server types.
 */
export interface FeatureGatePayload {
  kind: 'FEATURE_GATE';
  feature: keyof PlanFeatures;
  plan: PlanName;
  /** -1 for boolean gates that simply aren't available on this plan. */
  limit: number;
  suggestedPlan: string;
}

async function trackFeatureGateHit(
  context: Context,
  feature: keyof PlanFeatures,
  currentPlan: PlanName,
  suggestedPlan: string,
): Promise<void> {
  const userId = context.session?.user?.id;
  if (!userId) return;
  await track({
    event: 'feature_gate_hit',
    distinctId: userId,
    properties: {
      feature,
      current_plan: currentPlan,
      suggested_plan: suggestedPlan,
      surface: 'api',
    },
  });
}

/**
 * True when the request comes from an admin/super-admin session. Admin tier
 * is set in createContext via `session.user.role === 'admin' | 'super-admin'`,
 * and these sessions have no Stripe subscription (plan is null). Callers
 * MUST short-circuit on this before calling getEffectivePlan — otherwise
 * the null plan falls back to 'free' and our own admins hit free-tier caps.
 */
function isAdminBypass(context: Context): boolean {
  return context.subscription.tier === 'admin';
}

/**
 * Resolve the effective plan from the context subscription.
 * Falls back to 'free' for unknown or missing plans. Callers must check
 * `isAdminBypass(context)` first and skip the gate entirely for admins.
 */
function getEffectivePlan(context: Context): PlanName {
  const plan = context.subscription.plan;
  if (!plan || !(plan in PLAN_FEATURES)) return 'free';
  return plan as PlanName;
}

function throwFeatureGate(
  context: Context,
  feature: keyof PlanFeatures,
  plan: PlanName,
  limit: number,
): never {
  const desc = FEATURE_DESCRIPTIONS[feature];
  const suggestedPlan = desc.suggestedPlan;
  void trackFeatureGateHit(context, feature, plan, suggestedPlan);
  const payload: FeatureGatePayload = {
    kind: 'FEATURE_GATE',
    feature,
    plan,
    limit,
    suggestedPlan,
  };
  throw new ORPCError('FORBIDDEN', {
    message: `${desc.label} is not available on your current plan. ${desc.proBenefitCopy}`,
    data: payload,
  });
}

/**
 * Create a middleware that blocks the procedure if the user's plan
 * does not include the specified feature.
 *
 * @example
 * export const matchRouter = {
 *   score: protectedProcedure
 *     .use(requireFeature('brandMatching'))
 *     .input(z.object({ brandId: z.string() }))
 *     .handler(async ({ input }) => { ... }),
 * };
 */
export function requireFeature(feature: keyof PlanFeatures): GateMiddleware {
  return async ({ context, next }) => {
    const ctx = context as Context;
    if (isAdminBypass(ctx)) return next();
    const plan = getEffectivePlan(ctx);
    if (!hasFeature(plan, feature)) {
      throwFeatureGate(ctx, feature, plan, 0);
    }
    return next();
  };
}

/**
 * Create a middleware that checks a numeric feature limit against a current count.
 * The count is retrieved via a callback at request time.
 *
 * @example
 * export const dealRouter = {
 *   create: protectedProcedure
 *     .use(requireWithinLimit('deals', (ctx) =>
 *       ctx.db.deal.count({
 *         where: {
 *           athleteId: ctx.session.user.id,
 *           status: { notIn: ['COMPLETED', 'EXPIRED', 'CANCELLED'] },
 *         },
 *       }),
 *     ))
 *     .input(...)
 *     .handler(async ({ input }) => { ... }),
 * };
 */
export function requireWithinLimit(
  feature: keyof PlanFeatures,
  getCurrentCount: (context: Context) => Promise<number>,
): GateMiddleware {
  return async ({ context, next }) => {
    const ctx = context as Context;
    if (isAdminBypass(ctx)) return next();
    const plan = getEffectivePlan(ctx);
    const limit = getFeatureLimit(plan, feature);

    // -1 means unlimited
    if (limit === -1) return next();

    const currentCount = await getCurrentCount(ctx);
    if (currentCount >= limit) {
      throwFeatureGate(ctx, feature, plan, limit);
    }

    return next();
  };
}

const AI_EXTRACTION_RESET_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Specialized middleware for the `aiExtractions` quota. Reads
 * `User.aiExtractionsUsed` / `aiExtractionsResetAt` (already on the schema),
 * resets the counter on a rolling 30-day window, blocks at the per-plan
 * limit, and increments the counter atomically AFTER the handler resolves
 * successfully — failures (AI provider error, validation, etc.) don't
 * consume quota.
 *
 * Known race: two concurrent calls at used = limit - 1 can both pass the
 * pre-check and both increment, exceeding the cap by one. Acceptable at
 * this scale; a strict fix would use a conditional UPDATE returning rows.
 */
/**
 * Imperative cap check usable inside a handler (vs the middleware variants
 * above which only run before .handler()). Use this when the gate decision
 * depends on data that's only known after reading the existing row — e.g.
 * the deal.update path needs to know the OLD status to decide whether to
 * re-check the active-deal cap on a terminal → non-terminal transition.
 *
 * Throws the same ORPCError shape as the middleware so the client's
 * FEATURE_GATE interceptor handles it uniformly.
 */
export async function assertWithinDealLimit(
  context: Context,
  getCurrentCount: () => Promise<number>,
): Promise<void> {
  if (isAdminBypass(context)) return;
  const plan = getEffectivePlan(context);
  const limit = getFeatureLimit(plan, 'deals');
  if (limit === -1) return;
  const currentCount = await getCurrentCount();
  if (currentCount >= limit) {
    throwFeatureGate(context, 'deals', plan, limit);
  }
}

export function requireAiExtractionQuota(): GateMiddleware {
  return async ({ context, next }) => {
    const ctx = context as Context;
    if (isAdminBypass(ctx)) return next();
    const plan = getEffectivePlan(ctx);
    const limit = getFeatureLimit(plan, 'aiExtractions');

    // Unlimited plans skip the read entirely.
    if (limit === -1) return next();

    const userId = ctx.session?.user?.id;
    if (!userId) return next();

    const row = await ctx.db.user.findUnique({
      where: { id: userId },
      select: { aiExtractionsUsed: true, aiExtractionsResetAt: true },
    });
    if (!row) return next();

    const now = new Date();
    let used = row.aiExtractionsUsed;
    if (row.aiExtractionsResetAt <= now) {
      used = 0;
      await ctx.db.user.update({
        where: { id: userId },
        data: {
          aiExtractionsUsed: 0,
          aiExtractionsResetAt: new Date(now.getTime() + AI_EXTRACTION_RESET_WINDOW_MS),
        },
      });
    }

    if (used >= limit) {
      throwFeatureGate(ctx, 'aiExtractions', plan, limit);
    }

    const result = await next();

    // Atomic increment so we don't lose a count under interleaved updates.
    await ctx.db.user.update({
      where: { id: userId },
      data: { aiExtractionsUsed: { increment: 1 } },
    });

    return result;
  };
}
